"use client";

/**
 * CreditWarningToast — gentle notification for low balance warnings.
 *
 * Auto-dismisses after 5s. Shows when activeWarning is set in creditStore
 * and hasn't been manually dismissed.
 */

import { useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import { useCreditStore } from "@/stores/creditStore";
import { AlertTriangleIcon } from "@/components/icons/AlertTriangleIcon";
import { CloseIcon } from "@/components/icons/CloseIcon";

export function CreditWarningToast() {
  const activeWarning = useCreditStore((s) => s.activeWarning);
  const balance = useCreditStore((s) => s.balance);
  const dismissWarning = useCreditStore((s) => s.dismissWarning);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Auto-dismiss after 5 seconds
  useEffect(() => {
    if (activeWarning && !activeWarning.dismissedAt) {
      timerRef.current = setTimeout(() => {
        dismissWarning();
      }, 5000);
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [activeWarning, dismissWarning]);

  // Don't show if no warning or already dismissed
  if (!activeWarning || activeWarning.dismissedAt) return null;

  const severityStyles = {
    low: "border-[rgba(203,145,47,0.3)] bg-credit-warning-toast",
    critical: "border-[rgba(224,62,62,0.3)] bg-credit-warning-toast-2",
    grace: "border-[rgba(224,62,62,0.5)] bg-credit-bar",
  };

  const severityText = {
    low: "text-palette-amber",
    critical: "text-palette-red",
    grace: "text-palette-red",
  };

  return (
    <div
      className={cn(
        "fixed bottom-6 right-6 z-[80] max-w-sm",
        "rounded-16 border px-4 py-3 shadow-lg",
        "animate-in fade-in slide-in-from-bottom-3 duration-300",
        severityStyles[activeWarning.severity]
      )}
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">
          <AlertTriangleIcon
            width={16}
            height={16}
            strokeWidth="2"
            className={severityText[activeWarning.severity]}
          />
        </div>
        <div className="flex-1 min-w-0">
          <p
            className={cn(
              "text-3.5 font-medium",
              severityText[activeWarning.severity]
            )}
          >
            {activeWarning.severity === "grace"
              ? "Grace Period Active"
              : "Low Credits"}
          </p>
          <p className="text-3 text-fg-muted mt-0.5">
            {activeWarning.message ||
              `You have ${Math.round(balance)} credits remaining.`}
          </p>
        </div>
        <button
          onClick={dismissWarning}
          className="flex-shrink-0 p-1 rounded-12 text-fg-subtle hover:text-fg-default transition-colors"
        >
          <CloseIcon width={12} height={12} />
        </button>
      </div>
    </div>
  );
}
